<template>
  <div class="budget-transactions-list">
    <!-- Top KPI Summary Cards (dynamically reflects current filters) -->
    <v-row dense class="mb-4">
      <!-- 1. TUOTOT -->
      <v-col cols="12" sm="6" md="3">
        <v-card outlined class="pa-3 text-center border-left-green">
          <div
            class="text-caption green--text text--darken-3 text-uppercase font-weight-bold d-flex align-center justify-center"
          >
            <v-icon left x-small color="green darken-2">mdi-cash-plus</v-icon>
            Tuodut tuotot
          </div>
          <div
            class="text-h5 font-weight-black mt-1 green--text text--darken-2"
          >
            {{ formatCurrency(summaryStats.totalIncome) }}
          </div>
          <div class="text-caption grey--text mt-1">
            {{ summaryStats.incomeCount }} tositettamme
          </div>
        </v-card>
      </v-col>

      <!-- 2. KULUT -->
      <v-col cols="12" sm="6" md="3">
        <v-card outlined class="pa-3 text-center border-left-red">
          <div
            class="text-caption red--text text--darken-3 text-uppercase font-weight-bold d-flex align-center justify-center"
          >
            <v-icon left x-small color="red darken-2">mdi-cash-minus</v-icon>
            Tuodut kulut
          </div>
          <div class="text-h5 font-weight-black mt-1 red--text text--darken-2">
            {{ formatCurrency(summaryStats.totalExpense) }}
          </div>
          <div class="text-caption grey--text mt-1">
            {{ summaryStats.expenseCount }} tositettamme
          </div>
        </v-card>
      </v-col>

      <!-- 3. NETTO / TULOS -->
      <v-col cols="12" sm="6" md="3">
        <v-card outlined class="pa-3 text-center border-left-blue">
          <div
            class="text-caption text-uppercase font-weight-bold d-flex align-center justify-center"
            :class="
              summaryStats.netTotal >= 0
                ? 'primary--text'
                : 'deep-orange--text text--darken-2'
            "
          >
            <v-icon
              left
              x-small
              :color="summaryStats.netTotal >= 0 ? 'primary' : 'deep-orange'"
            >
              mdi-scale-balance
            </v-icon>
            Netto / Tulos
          </div>
          <div
            class="text-h5 font-weight-black mt-1"
            :class="
              summaryStats.netTotal >= 0
                ? 'green--text text--darken-2'
                : 'red--text text--darken-2'
            "
          >
            {{ formatCurrency(summaryStats.netTotal) }}
          </div>
          <div class="text-caption grey--text mt-1">
            Tuotot − Kulut
          </div>
        </v-card>
      </v-col>

      <!-- 4. TAPAHTUMAMÄÄRÄ -->
      <v-col cols="12" sm="6" md="3">
        <v-card outlined class="pa-3 text-center">
          <div
            class="text-caption grey--text text--darken-2 text-uppercase font-weight-bold d-flex align-center justify-center"
          >
            <v-icon left x-small color="grey darken-2">
              mdi-receipt-text-outline
            </v-icon>
            Tapahtumat & Rivit
          </div>
          <div class="text-h5 font-weight-black mt-1">
            {{ summaryStats.voucherCount }} kpl
          </div>
          <div class="text-caption grey--text mt-1">
            {{ summaryStats.rowCount }} kirjanpidon vientiriviä
            <span v-if="summaryStats.declinedCount > 0" class="orange--text">
              ({{ summaryStats.declinedCount }} hylätty)
            </span>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Main Card with Toolbar, Filters and Tables -->
    <v-card class="mb-4">
      <!-- Toolbar Header: View Mode Switcher and Actions -->
      <v-card-text class="pb-2">
        <v-row dense align="center" justify="space-between">
          <v-col cols="12" md="6" class="d-flex align-center flex-wrap">
            <div class="text-h6 font-weight-bold mr-4">
              Kirjanpidon tapahtumat {{ selectedYear }}
            </div>
            <!-- View Mode Switcher (Tapahtumittain vs Riveittäin) -->
            <v-btn-toggle
              v-model="viewMode"
              mandatory
              dense
              color="primary"
              class="my-1"
            >
              <v-btn value="vouchers" small>
                <v-icon left small>mdi-receipt-text</v-icon>
                Tapahtumittain ({{ filteredVouchers.length }})
              </v-btn>
              <v-btn value="rows" small>
                <v-icon left small>mdi-table-row</v-icon>
                Riveittäin ({{ filteredRows.length }})
              </v-btn>
            </v-btn-toggle>
          </v-col>

          <v-col cols="12" md="6" class="d-flex justify-end align-center flex-wrap">
            <v-btn
              outlined
              color="success"
              small
              class="mr-2 my-1"
              :disabled="filteredVouchers.length === 0"
              @click="exportToExcel"
            >
              <v-icon left small>mdi-file-excel</v-icon>
              Vie Exceliin (.xlsx)
            </v-btn>
            <v-btn
              v-if="canManage"
              color="primary"
              small
              class="my-1"
              title="Tuo Procountor-kirjanpitoraportti (.csv tai .xlsx)"
              @click="$emit('open-import')"
            >
              <v-icon left small>mdi-file-import</v-icon>
              Tuo Procountor-raportti
            </v-btn>
          </v-col>
        </v-row>

        <!-- Filters Row -->
        <v-row dense class="mt-2" align="center">
          <!-- Text search -->
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              placeholder="Hae kumppania, selitettä, tilinumeroa..."
              outlined
              dense
              clearable
              hide-details
            ></v-text-field>
          </v-col>

          <!-- Cost Center Filter -->
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="selectedProjectId"
              :items="projectOptions"
              item-text="fullName"
              item-value="id"
              label="Kustannuspaikka"
              outlined
              dense
              hide-details
            ></v-select>
          </v-col>

          <!-- Account Type Filter (Tuotto / Kulu / Investointi) -->
          <v-col cols="12" sm="4" md="2">
            <v-select
              v-model="selectedType"
              :items="typeOptions"
              item-text="text"
              item-value="value"
              label="Tilityyppi"
              outlined
              dense
              hide-details
            ></v-select>
          </v-col>

          <!-- Status Filter -->
          <v-col cols="12" sm="4" md="2">
            <v-select
              v-model="selectedStatus"
              :items="statusOptions"
              item-text="text"
              item-value="value"
              label="Tila"
              outlined
              dense
              hide-details
            ></v-select>
          </v-col>

          <!-- Month Filter -->
          <v-col cols="8" sm="3" md="1">
            <v-select
              v-model="selectedMonth"
              :items="monthOptions"
              item-text="text"
              item-value="value"
              label="Kk"
              outlined
              dense
              hide-details
            ></v-select>
          </v-col>

          <!-- Reset Filters Button -->
          <v-col cols="4" sm="1" md="1" class="text-right">
            <v-btn
              icon
              small
              title="Palauta suodattimet"
              @click="resetFilters"
              :disabled="!hasActiveFilters"
            >
              <v-icon color="grey darken-1">mdi-filter-off</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider></v-divider>

      <!-- EMPTY STATE -->
      <div
        v-if="filteredVouchers.length === 0"
        class="text-center py-10 grey--text"
      >
        <v-icon size="56" color="grey lighten-2">
          mdi-receipt-text-outline
        </v-icon>
        <div class="text-h6 mt-3 grey--text text--darken-1">
          Ei tapahtumia valituilla hakuehdoilla
        </div>
        <div class="text-caption grey--text mt-1">
          Kokeile tyhjentää suodattimet tai tuo Procountor-kirjanpitoraportti (.csv tai .xlsx).
        </div>
        <v-btn
          v-if="hasActiveFilters"
          outlined
          small
          color="primary"
          class="mt-3"
          @click="resetFilters"
        >
          Nollaa suodattimet
        </v-btn>
      </div>

      <!-- VIEW 1: TAPAHTUMITTAIN (VOUCHER-LEVEL) -->
      <v-data-table
        v-else-if="viewMode === 'vouchers'"
        :headers="voucherHeaders"
        :items="filteredVouchers"
        :items-per-page="itemsPerPage"
        :footer-props="{ itemsPerPageOptions: [15, 25, 50, 100, -1] }"
        show-expand
        single-expand
        :expanded.sync="expanded"
        item-key="id"
        class="elevation-0"
        dense
      >
        <!-- Expand toggle custom icon -->
        <template v-slot:item.data-table-expand="{ isExpanded, expand }">
          <v-btn icon x-small @click="expand(!isExpanded)">
            <v-icon small>
              {{ isExpanded ? "mdi-chevron-up" : "mdi-chevron-down" }}
            </v-icon>
          </v-btn>
        </template>

        <!-- Date -->
        <template v-slot:item.tositepvm="{ item }">
          <span class="text-caption font-mono font-weight-medium">
            {{ formatDisplayDate(item.tositepvm) }}
          </span>
        </template>

        <!-- Voucher Info -->
        <template v-slot:item.tosite="{ item }">
          <div class="d-flex align-center">
            <v-chip
              x-small
              outlined
              class="mr-1 font-weight-bold"
              :color="item.status === 'declined' ? 'grey' : 'primary'"
            >
              {{ item.tositelaji }}
            </v-chip>
            <span class="font-weight-bold font-mono">#{{ item.tositenro }}</span>
          </div>
          <div v-if="item.isSplit" class="mt-1">
            <v-chip x-small color="deep-purple" dark>
              <v-icon x-small left>mdi-call-split</v-icon>
              Jako {{ item.splitIndex }}/{{ item.splitCount }}
            </v-chip>
          </div>
        </template>

        <!-- Partner -->
        <template v-slot:item.partnerName="{ item }">
          <span class="font-weight-medium text-truncate d-inline-block" style="max-width: 220px">
            {{ item.partnerName || "-" }}
          </span>
        </template>

        <!-- Cost Center -->
        <template v-slot:item.projectName="{ item }">
          <v-chip x-small class="font-weight-medium">
            {{ getProjectName(item.projectId) }}
          </v-chip>
        </template>

        <!-- Account Info -->
        <template v-slot:item.accountInfo="{ item }">
          <div v-if="item.status === 'declined'" class="grey--text font-italic text-caption">
            {{ item.declineReason || "Hylätty" }}
          </div>
          <div v-else class="text-caption">
            <strong class="font-mono">{{ item.accountNumber }}</strong>
            <span v-if="item.subAccount" class="font-mono grey--text ml-1">({{ item.subAccount }})</span>
            <span class="ml-1 text-truncate">{{ item.accountName }}</span>
          </div>
        </template>

        <!-- Type -->
        <template v-slot:item.accountType="{ item }">
          <v-chip
            x-small
            :color="getTypeChipColor(getAccountType(item))"
            text-color="white"
            class="font-weight-medium"
          >
            {{ getTypeLabel(getAccountType(item)) }}
          </v-chip>
        </template>

        <!-- Amount -->
        <template v-slot:item.amount="{ item }">
          <span
            class="font-weight-bold font-mono"
            :class="item.status === 'declined' ? 'grey--text text--decoration-line-through' : item.amount > 0 ? 'green--text text--darken-2' : 'red--text text--darken-2'"
          >
            {{ formatCurrency(item.amount) }}
          </span>
        </template>

        <!-- Row Count -->
        <template v-slot:item.itemCount="{ item }">
          <v-chip x-small outlined color="grey darken-1">
            {{ item.items && item.items.length ? item.items.length : 1 }} kpl
          </v-chip>
        </template>

        <!-- Status -->
        <template v-slot:item.status="{ item }">
          <v-chip
            x-small
            :color="item.status === 'declined' ? 'grey lighten-2 grey--text text--darken-3' : 'green lighten-5 green--text text--darken-3'"
            class="font-weight-medium"
          >
            <v-icon x-small left>
              {{ item.status === 'declined' ? 'mdi-close-circle' : 'mdi-check-circle' }}
            </v-icon>
            {{ item.status === 'declined' ? 'Hylätty' : 'Kohdistettu' }}
          </v-chip>
        </template>

        <!-- Actions -->
        <template v-slot:item.actions="{ item }">
          <div class="d-flex align-center justify-center">
            <v-btn
              v-if="canManage"
              icon
              x-small
              color="secondary"
              title="Siirrä toiselle tilille"
              class="mr-1"
              @click.stop="openMoveDialog(item)"
            >
              <v-icon small>mdi-swap-horizontal</v-icon>
            </v-btn>
            <v-btn
              v-if="canManage && item.status === 'assigned'"
              icon
              x-small
              color="error"
              title="Poista kohdistus / Hylkää"
              @click.stop="confirmRemoveTransaction(item)"
            >
              <v-icon small>mdi-delete-outline</v-icon>
            </v-btn>
            <v-btn
              v-else-if="canManage && item.status === 'declined'"
              icon
              x-small
              color="primary"
              title="Palauta käsittelyyn"
              @click.stop="restoreTransaction(item)"
            >
              <v-icon small>mdi-restore</v-icon>
            </v-btn>
          </div>
        </template>

        <!-- EXPANDED ITEM (Vientirivien erittely) -->
        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="pa-3 grey lighten-5">
            <div class="d-flex justify-space-between align-center mb-2">
              <div class="font-weight-bold text-caption grey--text text--darken-3">
                <v-icon x-small left color="primary">mdi-format-list-bulleted</v-icon>
                Tositteen vientirivit ja ALV-erittely (#{{ item.tositenro }} - {{ item.partnerName }})
              </div>
              <div v-if="item.isSplit && item.originalTotalAmount" class="text-caption grey--text">
                Alkuperäinen koko tosite: <strong>{{ formatCurrency(item.originalTotalAmount) }}</strong>
              </div>
            </div>

            <!-- Items Table -->
            <v-simple-table dense class="elevation-1 rounded">
              <template v-slot:default>
                <thead>
                  <tr class="grey lighten-4">
                    <th class="text-left font-weight-bold text-caption">#</th>
                    <th class="text-left font-weight-bold text-caption">Vientiselite</th>
                    <th class="text-center font-weight-bold text-caption" style="width: 90px">ALV-%</th>
                    <th class="text-right font-weight-bold text-caption" style="width: 130px">Summa (€)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(it, itIdx) in (item.items && item.items.length ? item.items : [{ vientiselite: item.partnerName || '-', amount: item.amount }])"
                    :key="itIdx"
                  >
                    <td class="text-caption font-mono grey--text">{{ itIdx + 1 }}</td>
                    <td class="text-caption font-weight-medium">
                      {{ it.vientiselite || "(Ei vientiselitettä)" }}
                    </td>
                    <td class="text-center text-caption font-mono">
                      {{ it.alvPercent ? `${it.alvPercent} %` : "-" }}
                    </td>
                    <td
                      class="text-right text-caption font-weight-bold font-mono"
                      :class="it.amount > 0 ? 'green--text text--darken-2' : 'red--text text--darken-2'"
                    >
                      {{ formatCurrency(it.amount !== undefined ? it.amount : item.amount) }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>

            <div class="d-flex justify-space-between align-center mt-2 text-caption grey--text">
              <div>
                Tuotu: {{ formatDisplayDate(item.importedAt) }}
                <span v-if="item.importedBy && item.importedBy.user">
                  ({{ item.importedBy.user }})
                </span>
              </div>
              <div v-if="item.declineReason" class="red--text text--darken-2">
                Hylkäyksen syy: {{ item.declineReason }}
              </div>
            </div>
          </td>
        </template>
      </v-data-table>

      <!-- VIEW 2: RIVEITTÄIN (DETAILED LEDGER ENTRY ROWS) -->
      <v-data-table
        v-else-if="viewMode === 'rows'"
        :headers="rowHeaders"
        :items="filteredRows"
        :items-per-page="itemsPerPage"
        :footer-props="{ itemsPerPageOptions: [15, 25, 50, 100, -1] }"
        item-key="rowKey"
        class="elevation-0"
        dense
      >
        <!-- Date -->
        <template v-slot:item.tositepvm="{ item }">
          <span class="text-caption font-mono font-weight-medium">
            {{ formatDisplayDate(item.tositepvm) }}
          </span>
        </template>

        <!-- Voucher Info -->
        <template v-slot:item.tosite="{ item }">
          <div class="d-flex align-center">
            <v-chip
              x-small
              outlined
              class="mr-1 font-weight-bold"
              :color="item.status === 'declined' ? 'grey' : 'primary'"
            >
              {{ item.tositelaji }}
            </v-chip>
            <span class="font-weight-bold font-mono">#{{ item.tositenro }}</span>
          </div>
          <div v-if="item.isSplit" class="mt-1">
            <v-chip x-small color="deep-purple" dark>
              <v-icon x-small left>mdi-call-split</v-icon>
              {{ item.splitIndex }}/{{ item.splitCount }}
            </v-chip>
          </div>
        </template>

        <!-- Partner -->
        <template v-slot:item.partnerName="{ item }">
          <span class="font-weight-medium text-truncate d-inline-block" style="max-width: 180px">
            {{ item.partnerName || "-" }}
          </span>
        </template>

        <!-- Line description (Vientiselite) -->
        <template v-slot:item.vientiselite="{ item }">
          <span class="font-weight-medium text-truncate d-inline-block" style="max-width: 250px">
            {{ item.vientiselite || "(Ei vientiselitettä)" }}
          </span>
        </template>

        <!-- Cost Center -->
        <template v-slot:item.projectName="{ item }">
          <v-chip x-small class="font-weight-medium">
            {{ item.projectName }}
          </v-chip>
        </template>

        <!-- Account Info -->
        <template v-slot:item.accountInfo="{ item }">
          <div v-if="item.status === 'declined'" class="grey--text font-italic text-caption">
            {{ item.declineReason || "Hylätty" }}
          </div>
          <div v-else class="text-caption">
            <strong class="font-mono">{{ item.accountNumber }}</strong>
            <span v-if="item.subAccount" class="font-mono grey--text ml-1">({{ item.subAccount }})</span>
            <span class="ml-1 text-truncate">{{ item.accountName }}</span>
          </div>
        </template>

        <!-- Type -->
        <template v-slot:item.accountType="{ item }">
          <v-chip
            x-small
            :color="getTypeChipColor(item.accountType)"
            text-color="white"
            class="font-weight-medium"
          >
            {{ getTypeLabel(item.accountType) }}
          </v-chip>
        </template>

        <!-- ALV -->
        <template v-slot:item.alvPercent="{ item }">
          <span class="font-mono text-caption">
            {{ item.alvPercent ? `${item.alvPercent} %` : "-" }}
          </span>
        </template>

        <!-- Amount -->
        <template v-slot:item.amount="{ item }">
          <span
            class="font-weight-bold font-mono"
            :class="item.status === 'declined' ? 'grey--text text--decoration-line-through' : item.amount > 0 ? 'green--text text--darken-2' : 'red--text text--darken-2'"
          >
            {{ formatCurrency(item.amount) }}
          </span>
        </template>

        <!-- Status -->
        <template v-slot:item.status="{ item }">
          <v-chip
            x-small
            :color="item.status === 'declined' ? 'grey lighten-2 grey--text text--darken-3' : 'green lighten-5 green--text text--darken-3'"
            class="font-weight-medium"
          >
            {{ item.status === 'declined' ? 'Hylätty' : 'Kohdistettu' }}
          </v-chip>
        </template>

        <!-- Actions -->
        <template v-slot:item.actions="{ item }">
          <div class="d-flex align-center justify-center">
            <v-btn
              v-if="canManage"
              icon
              x-small
              color="secondary"
              title="Siirrä toiselle tilille"
              @click.stop="openMoveDialog(item.rawTx)"
            >
              <v-icon small>mdi-swap-horizontal</v-icon>
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Sub-dialog: Move Transaction to another Account -->
    <v-dialog v-model="moveDialog" max-width="500px" persistent>
      <v-card v-if="txToMove">
        <v-card-title class="headline primary white--text">
          Siirrä tosite toiselle tilille
        </v-card-title>
        <v-card-text class="pt-4">
          <div class="mb-3 text-body-2">
            Tosite:
            <strong>{{ txToMove.tositelaji }} #{{ txToMove.tositenro }}</strong>
            ({{ txToMove.partnerName }})<br />
            Summa: <strong>{{ formatCurrency(txToMove.amount) }}</strong>
          </div>

          <v-select
            v-model="targetProjectId"
            :items="projectItems"
            item-text="fullName"
            item-value="id"
            label="Kustannuspaikka"
            outlined
            dense
          ></v-select>

          <v-autocomplete
            v-model="targetAccountId"
            :items="getAccountsForTargetProject"
            item-text="displayName"
            item-value="id"
            label="Uusi budjettitili"
            outlined
            dense
          ></v-autocomplete>
        </v-card-text>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn text @click="moveDialog = false">Peruuta</v-btn>
          <v-btn
            color="primary"
            :disabled="!targetAccountId"
            :loading="moving"
            @click="executeMove"
          >
            Siirrä
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import { updateTransaction } from "@/services/budgetService";

export default {
  name: "BudgetTransactionsList",
  props: {
    transactions: {
      type: Array,
      default: () => [],
    },
    projects: {
      type: Array,
      default: () => [],
    },
    canManage: {
      type: Boolean,
      default: false,
    },
    selectedYear: {
      type: Number,
      default: 2026,
    },
    initialProjectId: {
      type: String,
      default: "all",
    },
  },
  data() {
    return {
      viewMode: "vouchers", // "vouchers" | "rows"
      search: "",
      selectedProjectId: "all",
      selectedType: "all",
      selectedStatus: "assigned",
      selectedMonth: "all",
      expanded: [],
      itemsPerPage: 25,

      // Move dialog
      moveDialog: false,
      txToMove: null,
      targetProjectId: "",
      targetAccountId: "",
      moving: false,
    };
  },
  watch: {
    initialProjectId: {
      immediate: true,
      handler(val) {
        if (val) this.selectedProjectId = val;
      },
    },
  },
  computed: {
    hasActiveFilters() {
      return (
        !!this.search ||
        this.selectedProjectId !== "all" ||
        this.selectedType !== "all" ||
        this.selectedStatus !== "assigned" ||
        this.selectedMonth !== "all"
      );
    },
    projectOptions() {
      const items = [{ id: "all", fullName: "Kaikki kustannuspaikat" }];
      this.projects.forEach((p) => {
        items.push({
          id: String(p.id || p.code),
          fullName: `${p.code} ${p.name}`,
        });
      });
      return items;
    },
    projectItems() {
      return this.projects.map((p) => ({
        id: String(p.id || p.code),
        fullName: `${p.code} ${p.name}`,
      }));
    },
    typeOptions() {
      return [
        { value: "all", text: "Kaikki tyypit" },
        { value: "balance", text: "🏛️ Tase (Oma pääoma)" },
        { value: "income", text: "🟢 Tuotot (Tulot)" },
        { value: "expense", text: "🔴 Kulut (Menot)" },
        { value: "investment", text: "🔵 Investoinnit" },
      ];
    },
    statusOptions() {
      return [
        { value: "all", text: "Kaikki tilat" },
        { value: "assigned", text: "Vain kohdistetut" },
        { value: "declined", text: "Vain hylätyt" },
      ];
    },
    monthOptions() {
      return [
        { value: "all", text: "Kaikki kk" },
        { value: "01", text: "Tammikuu" },
        { value: "02", text: "Helmikuu" },
        { value: "03", text: "Maaliskuu" },
        { value: "04", text: "Huhtikuu" },
        { value: "05", text: "Toukokuu" },
        { value: "06", text: "Kesäkuu" },
        { value: "07", text: "Heinäkuu" },
        { value: "08", text: "Elokuu" },
        { value: "09", text: "Syyskuu" },
        { value: "10", text: "Lokakuu" },
        { value: "11", text: "Marraskuu" },
        { value: "12", text: "Joulukuu" },
      ];
    },
    voucherHeaders() {
      return [
        { text: "", value: "data-table-expand", width: "40px" },
        { text: "Päivämäärä", value: "tositepvm", sortable: true, width: "110px" },
        { text: "Tosite", value: "tosite", sortable: true, width: "140px" },
        { text: "Kumppani", value: "partnerName", sortable: true },
        { text: "Kustannuspaikka", value: "projectName", sortable: true, width: "160px" },
        { text: "Budjettitili", value: "accountInfo", sortable: true },
        { text: "Tyyppi", value: "accountType", sortable: true, width: "110px" },
        { text: "Summa (€)", value: "amount", align: "right", sortable: true, width: "130px" },
        { text: "Rivit", value: "itemCount", align: "center", sortable: true, width: "70px" },
        { text: "Tila", value: "status", sortable: true, width: "110px" },
        { text: "Toiminnot", value: "actions", sortable: false, width: "90px", align: "center" },
      ];
    },
    rowHeaders() {
      return [
        { text: "Päivämäärä", value: "tositepvm", sortable: true, width: "110px" },
        { text: "Tosite", value: "tosite", sortable: true, width: "130px" },
        { text: "Kumppani", value: "partnerName", sortable: true },
        { text: "Vientiselite", value: "vientiselite", sortable: true },
        { text: "Kustannuspaikka", value: "projectName", sortable: true, width: "160px" },
        { text: "Budjettitili", value: "accountInfo", sortable: true },
        { text: "Tyyppi", value: "accountType", sortable: true, width: "110px" },
        { text: "ALV", value: "alvPercent", align: "center", sortable: true, width: "75px" },
        { text: "Summa (€)", value: "amount", align: "right", sortable: true, width: "130px" },
        { text: "Tila", value: "status", sortable: true, width: "100px" },
        { text: "Toiminnot", value: "actions", sortable: false, width: "70px", align: "center" },
      ];
    },
    getAccountsForTargetProject() {
      const proj = this.projects.find(
        (p) =>
          String(p.id) === String(this.targetProjectId) ||
          String(p.code) === String(this.targetProjectId),
      );
      if (!proj || !proj.accounts) return [];

      return proj.accounts
        .filter(
          (a) =>
            !a.isTotal &&
            !a.isTitle &&
            !a.isHeader &&
            (!a.accountNumber ||
              !/^\d{1,3}$/.test(String(a.accountNumber).trim())),
        )
        .map((a) => ({
          ...a,
          displayName: `${a.accountNumber ? a.accountNumber + " " : ""}${
            a.accountName
          }`,
        }));
    },
    filteredVouchers() {
      return this.transactions.filter((tx) => {
        // Project filter
        if (this.selectedProjectId !== "all") {
          if (String(tx.projectId) !== String(this.selectedProjectId)) {
            return false;
          }
        }

        // Status filter
        if (this.selectedStatus !== "all") {
          const status = tx.status || "assigned";
          if (status !== this.selectedStatus) return false;
        }

        // Account type filter
        if (this.selectedType !== "all") {
          const type = this.getAccountType(tx);
          if (type !== this.selectedType) return false;
        }

        // Month filter
        if (this.selectedMonth !== "all") {
          const month = this.extractMonth(tx.tositepvm);
          if (month !== this.selectedMonth) return false;
        }

        // Search text
        if (this.search && this.search.trim()) {
          const q = this.search.trim().toLowerCase();
          const pName = (tx.partnerName || "").toLowerCase();
          const tNo = String(tx.tositenro || "").toLowerCase();
          const accNo = String(tx.accountNumber || "").toLowerCase();
          const accSub = String(tx.subAccount || "").toLowerCase();
          const accName = (tx.accountName || "").toLowerCase();
          const pJName = this.getProjectName(tx.projectId).toLowerCase();

          // Also check inside items
          const itemMatch = (tx.items || []).some((it) =>
            (it.vientiselite || "").toLowerCase().includes(q),
          );

          if (
            !pName.includes(q) &&
            !tNo.includes(q) &&
            !accNo.includes(q) &&
            !accSub.includes(q) &&
            !accName.includes(q) &&
            !pJName.includes(q) &&
            !itemMatch
          ) {
            return false;
          }
        }

        return true;
      });
    },
    filteredRows() {
      const rows = [];
      this.filteredVouchers.forEach((tx) => {
        const items =
          tx.items && tx.items.length
            ? tx.items
            : [{ vientiselite: tx.partnerName || "-", amount: tx.amount }];

        items.forEach((it, idx) => {
          // If search active, check if either tx matches or this specific item matches
          if (this.search && this.search.trim()) {
            const q = this.search.trim().toLowerCase();
            const itSelite = (it.vientiselite || "").toLowerCase();
            const pName = (tx.partnerName || "").toLowerCase();
            const tNo = String(tx.tositenro || "").toLowerCase();
            const accNo = String(tx.accountNumber || "").toLowerCase();
            const accName = (tx.accountName || "").toLowerCase();

            if (
              !itSelite.includes(q) &&
              !pName.includes(q) &&
              !tNo.includes(q) &&
              !accNo.includes(q) &&
              !accName.includes(q)
            ) {
              return;
            }
          }

          rows.push({
            rowKey: `${tx.id}_row_${idx}`,
            id: tx.id,
            tositepvm: tx.tositepvm,
            tositelaji: tx.tositelaji,
            tositenro: tx.tositenro,
            partnerName: tx.partnerName,
            vientiselite: it.vientiselite || tx.partnerName || "-",
            projectId: tx.projectId,
            projectName: this.getProjectName(tx.projectId),
            accountId: tx.accountId,
            accountNumber: tx.accountNumber,
            subAccount: tx.subAccount,
            accountName: tx.accountName,
            accountType: this.getAccountType(tx),
            alvPercent: it.alvPercent || "",
            amount: it.amount !== undefined ? it.amount : tx.amount,
            status: tx.status,
            declineReason: tx.declineReason,
            isSplit: tx.isSplit,
            splitIndex: tx.splitIndex,
            splitCount: tx.splitCount,
            rawTx: tx,
          });
        });
      });

      return rows;
    },
    summaryStats() {
      let totalIncome = 0;
      let totalExpense = 0;
      let incomeCount = 0;
      let expenseCount = 0;
      let declinedCount = 0;

      this.filteredVouchers.forEach((tx) => {
        if (tx.status === "declined") {
          declinedCount++;
          return;
        }

        let amt = parseFloat(tx.amount) || 0;
        if (
          amt > 0 &&
          (tx.tositelaji === "Ostolasku" || tx.tositelaji === "Kululasku")
        ) {
          amt = -amt;
        }
        if (amt > 0) {
          totalIncome += amt;
          incomeCount++;
        } else if (amt < 0) {
          totalExpense += amt;
          expenseCount++;
        }
      });

      const netTotal =
        totalExpense < 0
          ? totalIncome + totalExpense
          : totalIncome - totalExpense;

      return {
        totalIncome: Math.round(totalIncome * 100) / 100,
        incomeCount,
        totalExpense: Math.round(totalExpense * 100) / 100,
        expenseCount,
        netTotal: Math.round(netTotal * 100) / 100,
        voucherCount: this.filteredVouchers.length,
        rowCount: this.filteredRows.length,
        declinedCount,
      };
    },
  },
  methods: {
    resetFilters() {
      this.search = "";
      this.selectedProjectId = "all";
      this.selectedType = "all";
      this.selectedStatus = "assigned";
      this.selectedMonth = "all";
    },
    getProjectName(projectId) {
      if (!projectId) return "-";
      const proj = this.projects.find(
        (p) =>
          String(p.id) === String(projectId) ||
          String(p.code) === String(projectId),
      );
      if (proj) return `${proj.code} ${proj.name}`;
      return `Osasto ${projectId}`;
    },
    getAccountType(tx) {
      if (tx.accountType) {
        if (tx.accountType === "equity") return "balance";
        return tx.accountType;
      }

      const proj = this.projects.find(
        (p) =>
          String(p.id) === String(tx.projectId) ||
          String(p.code) === String(tx.projectId),
      );
      if (proj && proj.accounts) {
        const acc = proj.accounts.find(
          (a) =>
            a.id === tx.accountId ||
            (a.accountNumber && a.accountNumber === tx.accountNumber),
        );
        if (acc && acc.type) {
          if (acc.type === "equity") return "balance";
          return acc.type;
        }
      }

      const accNum = String(tx.accountNumber || "").trim();
      if (accNum.startsWith("1") || accNum.startsWith("2")) return "balance";

      const amt = parseFloat(tx.amount) || 0;
      return amt >= 0 ? "income" : "expense";
    },
    getTypeLabel(type) {
      switch (type) {
        case "balance":
        case "equity":
          return "Tase";
        case "income":
          return "Tuotto";
        case "expense":
          return "Kulu";
        case "investment":
          return "Investointi";
        default:
          return "Muu";
      }
    },
    getTypeChipColor(type) {
      switch (type) {
        case "balance":
        case "equity":
          return "teal darken-3";
        case "income":
          return "green darken-2";
        case "expense":
          return "red darken-2";
        case "investment":
          return "deep-purple darken-1";
        default:
          return "grey darken-1";
      }
    },
    formatCurrency(val) {
      if (val === null || val === undefined || isNaN(val)) return "0,00 €";
      return (
        Number(val).toLocaleString("fi-FI", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + " €"
      );
    },
    formatDisplayDate(val) {
      if (!val) return "-";
      // Handle YYYY-MM-DD
      if (/^\d{4}-\d{2}-\d{2}/.test(val)) {
        const parts = val.substring(0, 10).split("-");
        return `${parts[2]}.${parts[1]}.${parts[0]}`;
      }
      return val;
    },
    extractMonth(val) {
      if (!val) return "";
      if (/^\d{4}-\d{2}-\d{2}/.test(val)) {
        return val.substring(5, 7);
      }
      if (/^\d{2}\.\d{2}\.\d{4}/.test(val)) {
        return val.substring(3, 5);
      }
      return "";
    },
    openMoveDialog(tx) {
      this.txToMove = tx;
      this.targetProjectId = String(tx.projectId || "");
      this.targetAccountId = "";
      this.moveDialog = true;
    },
    async executeMove() {
      if (!this.txToMove || !this.targetAccountId) return;
      this.moving = true;
      try {
        const accs = this.getAccountsForTargetProject;
        const targetAcc = accs.find((a) => a.id === this.targetAccountId);
        const isTargetExpense =
          targetAcc &&
          (targetAcc.type === "expense" ||
            (targetAcc.type === "investment" &&
              targetAcc.accountNumber &&
              String(targetAcc.accountNumber).startsWith("7")));
        const isTargetIncome = targetAcc && targetAcc.type === "income";

        let newAmount = parseFloat(this.txToMove.amount) || 0;
        if (isTargetExpense && newAmount > 0) {
          newAmount = -Math.abs(newAmount);
        } else if (isTargetIncome && newAmount < 0) {
          newAmount = Math.abs(newAmount);
        }

        await updateTransaction(
          this.txToMove.id,
          {
            projectId: this.targetProjectId,
            accountId: this.targetAccountId,
            accountNumber: targetAcc ? targetAcc.accountNumber : "",
            accountName: targetAcc ? targetAcc.accountName : "",
            subAccount: targetAcc ? targetAcc.subAccount || "" : "",
            amount: newAmount,
          },
          {
            actionType: "TRANSACTION_UPDATE",
            summary: `Tosite #${this.txToMove.tositenro} siirretty tilille ${
              targetAcc?.accountNumber || ""
            } ${targetAcc?.accountName || ""}`,
            details: `Kustannuspaikka: ${
              this.targetProjectId
            }, summa: ${this.formatCurrency(newAmount)}`,
          },
        );

        this.$toast?.success?.("Tosite siirretty onnistuneesti!");
        this.moveDialog = false;
        this.txToMove = null;
      } catch (err) {
        console.error("Error moving transaction:", err);
        this.$toast?.error?.("Siirto epäonnistui");
      } finally {
        this.moving = false;
      }
    },
    async confirmRemoveTransaction(tx) {
      if (
        confirm(
          `Haluatko varmasti poistaa kohdistuksen tositteelta #${tx.tositenro}? Tosite merkitään hylätyksi.`,
        )
      ) {
        try {
          await updateTransaction(
            tx.id,
            {
              status: "declined",
              declineReason: "Kohdistus poistettu tapahtumalokerosta",
            },
            {
              actionType: "TRANSACTION_UPDATE",
              summary: `Tositteen #${tx.tositenro} kohdistus poistettu`,
              details: `Tosite merkitty hylätyksi. Summa: ${this.formatCurrency(
                tx.amount,
              )}`,
            },
          );
          this.$toast?.success?.("Kohdistus poistettu!");
        } catch (err) {
          console.error("Error removing transaction:", err);
          this.$toast?.error?.("Poisto epäonnistui");
        }
      }
    },
    async restoreTransaction(tx) {
      try {
        await updateTransaction(
          tx.id,
          {
            status: "assigned",
            declineReason: "",
          },
          {
            actionType: "TRANSACTION_UPDATE",
            summary: `Hylätty tosite #${tx.tositenro} palautettu kohdistetuksi`,
            details: `Palautettu tilille ${tx.accountNumber || ""} ${
              tx.accountName || ""
            }`,
          },
        );
        this.$toast?.success?.("Tosite palautettu kohdistetuksi!");
      } catch (err) {
        console.error("Error restoring transaction:", err);
        this.$toast?.error?.("Palautus epäonnistui");
      }
    },
    exportToExcel() {
      const wb = XLSX.utils.book_new();

      const exportRows = [
        [
          "Päivämäärä",
          "Tositelaji",
          "Tositenumero",
          "Kumppani",
          "Vientiselite",
          "Kustannuspaikka",
          "Tilinumero",
          "Kirjanpidon tili",
          "Tilin nimi",
          "Tilityyppi",
          "ALV-%",
          "Summa (€)",
          "Tila",
          "Hylkäyksen syy",
        ],
      ];

      this.filteredRows.forEach((r) => {
        exportRows.push([
          this.formatDisplayDate(r.tositepvm),
          r.tositelaji || "",
          r.tositenro || "",
          r.partnerName || "",
          r.vientiselite || "",
          r.projectName || "",
          r.accountNumber || "",
          r.subAccount || "",
          r.accountName || "",
          this.getTypeLabel(r.accountType),
          r.alvPercent || "",
          r.amount !== undefined ? r.amount : 0,
          r.status === "declined" ? "Hylätty" : "Kohdistettu",
          r.declineReason || "",
        ]);
      });

      const ws = XLSX.utils.aoa_to_sheet(exportRows);
      XLSX.utils.book_append_sheet(wb, ws, "KP_Tapahtumat");
      XLSX.writeFile(wb, `KP_Tapahtumat_${this.selectedYear}.xlsx`);
      this.$toast?.success?.("Tapahtumat viety Exceliin!");
    },
  },
};
</script>

<style scoped>
.font-mono {
  font-family: monospace;
}
.border-left-green {
  border-left: 4px solid #2e7d32 !important;
}
.border-left-red {
  border-left: 4px solid #c62828 !important;
}
.border-left-blue {
  border-left: 4px solid #1976d2 !important;
}
</style>
